'use client'

import React from 'react'
import Image from 'next/image'
import Slider from 'react-slick'
import { Star } from 'lucide-react'
import { cn } from '@/lib/utils'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

export type Testimonial = {
  name: string
  role: string
  avatar?: string
  quote: string
  rating?: number
}

const testimonials: Testimonial[] = [
  {
    name: 'Emma Johnson',
    role: 'High School Math Teacher',
    avatar: 'https://i.pravatar.cc/120?img=5',
    quote:
      'EduBridgeInsight turns messy gradebooks into clear visuals my students and parents actually understand. It saves me hours every week.',
    rating: 5,
  },
  {
    name: 'Michael Chen',
    role: 'Department Head, Science',
    avatar: 'https://i.pravatar.cc/120?img=11',
    quote:
      'The AI analysis highlights patterns we used to miss. We make faster, evidence-based decisions for interventions.',
    rating: 5,
  },
  {
    name: 'Sophia Martinez',
    role: 'Assistant Principal',
    avatar: 'https://i.pravatar.cc/120?img=15',
    quote:
      'Beautiful reports in minutes. Our team uses the charts in every meeting — no more spreadsheets on the projector.',
    rating: 5,
  },
]

function Stars({ value = 5 }: { value?: number }) {
  return (
    <div className='flex items-center gap-1 text-[hsl(var(--primary-blue))]'>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn('h-4 w-4', i < value ? 'fill-current' : 'opacity-30')}
        />
      ))}
    </div>
  )
}

export default function TestimonialsCarousel() {
  const settings: Slider['props'] = {
    dots: true,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 5000,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    adaptiveHeight: false,
    responsive: [
      { breakpoint: 1280, settings: { slidesToShow: 2 } },
      { breakpoint: 768, settings: { slidesToShow: 1 } },
    ],
  }

  return (
    <div className='relative'>
      <Slider className='testimonials-slider' {...settings}>
        {testimonials.map((t, idx) => (
          <div key={idx} className='px-3'>
            <div className='h-full min-h-[220px] md:min-h-[240px] rounded-xl border border-border bg-background p-6 shadow-subtle flex flex-col justify-between'>
              <div className='flex items-center gap-4'>
                <div className='h-12 w-12 overflow-hidden rounded-full border border-border/60'>
                  {t.avatar ? (
                    <Image src={t.avatar} alt={t.name} width={48} height={48} />
                  ) : (
                    <div className='h-full w-full bg-muted' />
                  )}
                </div>
                <div>
                  <div className='font-semibold text-foreground'>{t.name}</div>
                  <div className='text-xs text-muted-foreground'>{t.role}</div>
                </div>
              </div>

              <div className='mt-4 flex-1 text-sm leading-relaxed text-muted-foreground'>
                “{t.quote}”
              </div>

              <div className='mt-4'>
                <Stars value={t.rating ?? 5} />
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  )
}
